import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { reaction } from 'mobx';
import classNames from 'classnames';

import {
    useSession,

    Sizes,
    Colours,
    PrimaryButton,
    MessageBannerSuccess,
    MessageBannerError,
    AnimatedFadeContainer,
    Question,
    QuestionPhoneNumber,
    CircleSpinner,
    Form,

    scrollToTop,
} from '@ratehub/base-ui';


function PageProfileDetails({ model, portalBannerId, ...otherProps }) {
    const sessionStore = useSession();

    // Problem: sessionStore.pushProfileUpdate mutates model.values.location object
    //     this will trigger handleChange and set hasSuceeded to false
    //     This results in disappearance of MessageBannerSuccess
    // Solution: Capture the initial values from model.values at the beginning of lifecycle
    //     Do not let the values get updated from sessionStore.pushProfileUpdate call
    const [ initialValues ] = useState(model.values);

    // REQUIREMENT: when a banner is first shown, scroll to the top of the screen.
    useEffect(
        () =>
            reaction(
                () => model.hasSuceeded || model.hasError,
                value => {
                    value && scrollToTop(true);
                },
                { name: 'Scroll to top for banner' },
            ),
        [],
    );

    // REQUIREMENT: when the user changes a value, clear all banners
    const handleChange = useCallback(model.clearStatuses, [ model ]);

    return (
        <>
            <If condition={!model.isReady}>
                <CircleSpinner />
            </If>

            {
                // REQUIREMENT: our MessageBanner has to extend across the entire width of the screen.
                // SOLUTION: we ASSUME we're rendered with a <div /> with a known ID we can portal into.
                createPortal(
                    <>
                        <AnimatedFadeContainer isVisible={model.hasSuceeded}>
                            <MessageBannerSuccess
                                id="banner-profile-saved"
                                message={(
                                    <FormattedMessage
                                        id="dashboard.profile-details.successBanner"
                                        defaultMessage="Profile has been saved"
                                    />
                                )}
                            />
                        </AnimatedFadeContainer>
                        <AnimatedFadeContainer isVisible={model.hasError}>
                            <MessageBannerError
                                message={(
                                    <FormattedMessage
                                        id="dashboard.profile-details.errorBanner"
                                        defaultMessage="Something went wrong"
                                    />
                                )}
                            />
                        </AnimatedFadeContainer>
                    </>,
                    document.getElementById(portalBannerId),
                )
            }

            <div {...otherProps}>
                <h1
                    className={classNames(
                        'rh-title-2xl',
                        'rh-mt-0',
                        'rh-mb-3',
                        'title',
                    )}
                >
                    <FormattedMessage
                        id="dashboard.profile-details.pageTitle"
                        defaultMessage="Personal information"
                    />
                </h1>

                <Form
                    onSubmit={model.submit}
                    onChange={handleChange}
                    isAutoFocusEnabled={false}
                >
                    <Question
                        field={{
                            name: 'firstName',
                            type: 'string',
                            value: initialValues?.firstName,
                        }}
                        labelMessage={(
                            <FormattedMessage
                                id="dashboard.profile-details.firstNameLabel"
                                defaultMessage="First name"
                            />
                        )}
                    />

                    <Question
                        field={{
                            name: 'lastName',
                            type: 'string',
                            value: initialValues?.lastName,
                        }}
                        labelMessage={(
                            <FormattedMessage
                                id="dashboard.profile-details.lastNameLabel"
                                defaultMessage="Last name"
                            />
                        )}
                    />

                    <QuestionPhoneNumber
                        value={initialValues?.phone}
                        fieldOptions={{
                            required: false,
                        }}
                    />

                    <Question
                        field={{
                            name: 'location',
                            type: 'city',
                            value: initialValues?.location,
                        }}
                        labelMessage={(
                            <FormattedMessage
                                id="dashboard.profile-details.locationLabel"
                                defaultMessage="Location"
                            />
                        )}
                    />

                    <Question
                        field={{
                            name: 'email-input',
                            type: 'email',
                            value: sessionStore.email,
                        }}
                        labelMessage={(
                            <FormattedMessage
                                id="dashboard.profile-details.email"
                                defaultMessage="Email address"
                            />
                        )}
                        readOnly={true}
                    />

                    <PrimaryButton
                        type="submit"
                        data-name="dashboard-profileDetails-submit"
                        message={(
                            <FormattedMessage
                                id="dashboard.profile-details.submit"
                                defaultMessage="Save"
                            />
                        )}
                    />
                </Form>
            </div>
        </>
    );
}

PageProfileDetails.propTypes = {
    model: PropTypes.object.isRequired,
    portalBannerId: PropTypes.string.isRequired,
};

PageProfileDetails.defaultProps = {};

const StyledPageProfileDetails = styled(observer(PageProfileDetails))`
    max-width: 30em;
    min-width: 15em;

    margin: ${Sizes.SPACING.ONE_AND_A_HALF} auto;

    /* WORKAROUND: IE11 usually ignores max-width if used w/ margin 0 auto. */
    width: 100%;

    > .title {
        text-align: center;
    }

    /*
        Not my preferred way of doing this, but only temporary.
        It's a decendant of the QuestionContainer
    */
    #emailField {
        margin: ${Sizes.SPACING.HALF} 0;
        color: ${Colours.STONE_DARKEST};
    }
`;

export default StyledPageProfileDetails;
